import { useState } from "react"
import { createPortal } from "react-dom"
import { StartPopupData, StartPopupSection } from "../types/entities/startPopup"
import Loader from "./Loader"
import SwipeSheetPanel from "./SwipeSheetPanel"

const POPUP_SHEET_CLASS =
    "relative flex w-full max-h-[90dvh] flex-col overflow-hidden rounded-t-[28px] md:h-auto md:max-h-[min(92svh,820px)] md:w-full md:max-w-[428px] md:rounded-[36px]"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type TermsPopupProps = {
    data: StartPopupData
    onConfirm: () => void
    onDismiss?: () => void
    bodyAlign?: "center" | "left"
}

function getBlocks(data: StartPopupData): StartPopupSection[] {
    if (data.sections?.length) {
        return data.sections.map((section, index) => ({
            ...section,
            images: section.images?.length ? section.images : data.images?.[index] ? [data.images[index]] : [],
        }))
    }

    return [
        {
            title: "",
            body: data.body ?? "",
            images: data.images?.[0] ? [data.images[0]] : [],
        },
    ]
}

function SectionImage({ src }: { src: string }) {
    const [loaded, setLoaded] = useState(false)

    return (
        <div className="relative w-full">
            <img
                src={src}
                alt=""
                className={`h-[160px] w-full rounded-[18px] object-cover transition-opacity md:h-[180px] ${
                    loaded ? "opacity-100" : "opacity-0"
                }`}
                onLoad={() => setLoaded(true)}
            />
            {!loaded && (
                <div className="absolute inset-0 flex min-h-[160px] items-center justify-center md:min-h-[180px]">
                    <Loader />
                </div>
            )}
        </div>
    )
}

export default function TermsPopup({ data, onConfirm, onDismiss, bodyAlign = "center" }: TermsPopupProps) {
    const [checked, setChecked] = useState(false)
    const disabled = Boolean(data.agree) && !checked
    const blocks = getBlocks(data)
    const sectioned = Boolean(data.sections?.length)

    return createPortal(
        <div
            className="fixed inset-0 z-[120] flex items-end justify-center overflow-hidden md:items-center md:px-4"
            style={{ background: "rgba(10,18,42,0.8)", backdropFilter: "blur(6px)" }}
            onMouseDown={e => {
                if (e.target === e.currentTarget) (onDismiss ?? onConfirm)()
            }}>
            <SwipeSheetPanel
                onDismiss={onDismiss ?? onConfirm}
                className={POPUP_SHEET_CLASS}
                style={{
                    background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)",
                    border: "1.5px solid rgba(255,255,255,0.12)",
                }}
                motionProps={{
                    initial: { y: "100%", opacity: 0 },
                    animate: { y: 0, opacity: 1 },
                    exit: { y: "100%", opacity: 0 },
                    transition: { type: "spring", stiffness: 320, damping: 32 },
                    onMouseDown: e => e.stopPropagation(),
                }}>
                <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
                    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-5 pt-1 md:gap-6 md:px-10 md:pt-10">
                        <h2
                            className="w-full text-center text-[22px] leading-tight font-bold text-white md:text-[26px]"
                            style={{ fontFamily: DISPLAY, letterSpacing: "-0.02em" }}>
                            {data.title}
                        </h2>

                        <div className={`flex w-full flex-col ${sectioned ? "gap-5" : "gap-4"}`}>
                            {blocks.map((block, index) => (
                                <div key={block.slug ?? index} className="flex w-full flex-col gap-3">
                                    {sectioned && index > 0 && <div className="border-t border-white/10" />}
                                    {block.title ? (
                                        <p
                                            className="text-[11px] uppercase tracking-[0.08em] text-white/40"
                                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                                            {block.title}
                                        </p>
                                    ) : null}
                                    {block.body ? (
                                        <p
                                            className={`whitespace-pre-line text-[15px] leading-relaxed text-white/70 ${
                                                bodyAlign === "left" || sectioned ? "text-left" : "text-center"
                                            }`}
                                            style={{ fontFamily: BODY }}>
                                            {block.body}
                                        </p>
                                    ) : null}
                                    {block.images?.map(src => (
                                        <SectionImage key={src} src={src} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex shrink-0 flex-col gap-4 px-5 pb-[max(1.25rem,var(--safe-bottom))] pt-4 md:px-10 md:pb-10">
                        {data.agree && (
                            <label className="flex w-full cursor-pointer items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={e => setChecked(e.target.checked)}
                                    className="h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-white/30 shadow transition-all checked:bg-primary hover:border-white/50"
                                />
                                <span className="text-[13px] text-white/80" style={{ fontFamily: BODY }}>
                                    Принимаю
                                </span>
                            </label>
                        )}

                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={disabled}
                            className="flex h-12 w-full shrink-0 cursor-pointer items-center justify-center rounded-full bg-[#349be6] px-8 text-base font-bold text-white transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                            style={{
                                fontFamily: MONO,
                                boxShadow: disabled ? "none" : "0 0 20px rgba(52,155,230,0.4)",
                            }}>
                            {data.button}
                        </button>
                    </div>
                </div>
            </SwipeSheetPanel>
        </div>,
        document.body,
    )
}
